import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Truck, Save, Loader2, Info, AlertTriangle, ShieldCheck, MessageSquare } from 'lucide-react';
import { companyApi, DeliveryDueSettings as DeliveryDueSettingsType } from '@/services/companyApi';
import { getCurrencySymbol } from '@/utils/currency';

const AVAILABLE_ROLES = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'BRANCH_ADMIN', label: 'Branch Admin' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'COUNTER_ADMIN', label: 'Counter Admin' },
  { value: 'STOCK_ADMIN', label: 'Stock Admin' },
  { value: 'SERVICE_ADMIN', label: 'Service Admin' },
  { value: 'TECHNICIAN', label: 'Technician' },
  { value: 'RECEPTIONIST', label: 'Receptionist' },
];

export default function DeliveryDueSettings() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'off' | 'warn' | 'strict'>('off');
  const [dueLimit, setDueLimit] = useState('0');
  const [overrideRoles, setOverrideRoles] = useState<string[]>(['ADMIN', 'MANAGER', 'BRANCH_ADMIN']);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['deliveryDueSettings'],
    queryFn: companyApi.getDeliveryDueSettings,
  });

  const updateMutation = useMutation({
    mutationFn: (data: DeliveryDueSettingsType) => companyApi.updateDeliveryDueSettings(data),
    onSuccess: () => {
      toast.success('Delivery due settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['deliveryDueSettings'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });

  useEffect(() => {
    if (settings) {
      setMode(settings.deliveryDueMode as 'off' | 'warn' | 'strict');
      setDueLimit(String(settings.deliveryDueLimit));
      setOverrideRoles(settings.deliveryDueOverrideRoles);
    }
  }, [settings]);

  const checkChanges = (newMode: string, newLimit: string, newRoles: string[]) => {
    if (!settings) return;
    setHasChanges(
      newMode !== settings.deliveryDueMode ||
      newLimit !== String(settings.deliveryDueLimit) ||
      JSON.stringify(newRoles.sort()) !== JSON.stringify(settings.deliveryDueOverrideRoles.sort())
    );
  };

  const handleModeChange = (newMode: 'off' | 'warn' | 'strict') => {
    setMode(newMode);
    checkChanges(newMode, dueLimit, overrideRoles);
  };

  const handleLimitChange = (value: string) => {
    setDueLimit(value);
    checkChanges(mode, value, overrideRoles);
  };

  const handleRoleToggle = (role: string) => {
    const newRoles = overrideRoles.includes(role)
      ? overrideRoles.filter((r) => r !== role)
      : [...overrideRoles, role];
    setOverrideRoles(newRoles);
    checkChanges(mode, dueLimit, newRoles);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      deliveryDueMode: mode,
      deliveryDueLimit: parseFloat(dueLimit) || 0,
      deliveryDueOverrideRoles: overrideRoles,
    });
  };

  const handleReset = () => {
    if (settings) {
      setMode(settings.deliveryDueMode as 'off' | 'warn' | 'strict');
      setDueLimit(String(settings.deliveryDueLimit));
      setOverrideRoles(settings.deliveryDueOverrideRoles);
      setHasChanges(false);
    }
  };

  // Delivery OTP settings
  const { data: otpSettings, isLoading: isLoadingOtp } = useQuery({
    queryKey: ['deliveryOtpSettings'],
    queryFn: companyApi.getDeliveryOtpSettings,
  });

  const otpMutation = useMutation({
    mutationFn: (deliveryOtpRequired: boolean) =>
      companyApi.updateDeliveryOtpSettings({ deliveryOtpRequired }),
    onSuccess: () => {
      toast.success('Delivery OTP setting updated');
      queryClient.invalidateQueries({ queryKey: ['deliveryOtpSettings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update OTP setting');
    },
  });

  if (isLoading || isLoadingOtp) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* OTP Verification Setting */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Delivery OTP Verification</h2>
            <p className="text-sm text-gray-500">
              Require OTP verification via WhatsApp before delivering devices to customers
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {otpSettings?.deliveryOtpRequired !== false ? 'OTP Required' : 'OTP Disabled'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {otpSettings?.deliveryOtpRequired !== false
                  ? 'A 6-digit code is sent to the customer\'s WhatsApp before delivery'
                  : 'Devices can be delivered without OTP verification'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => otpMutation.mutate(!(otpSettings?.deliveryOtpRequired !== false))}
              disabled={otpMutation.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                otpSettings?.deliveryOtpRequired !== false ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  otpSettings?.deliveryOtpRequired !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Delivery Due Control Header */}
      <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Truck className="w-6 h-6 text-purple-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Delivery Due Control</h2>
          <p className="text-sm text-gray-500">
            Control device delivery based on outstanding payment balance
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Delivery Mode Selection */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Delivery Control Mode</h3>

          <div className="space-y-4">
            {/* Off */}
            <label
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                mode === 'off'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="deliveryMode"
                checked={mode === 'off'}
                onChange={() => handleModeChange('off')}
                className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Off</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    No Restriction
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Devices can be delivered regardless of outstanding balance. Suitable for trust-based shops or credit customers.
                </p>
              </div>
            </label>

            {/* Warn */}
            <label
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                mode === 'warn'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="deliveryMode"
                checked={mode === 'warn'}
                onChange={() => handleModeChange('warn')}
                className="mt-1 w-4 h-4 text-amber-600 focus:ring-amber-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Warn</span>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Shows a warning with the outstanding due amount when delivering. Employee must acknowledge and provide a reason to proceed. All overrides are logged for audit.
                </p>
              </div>
            </label>

            {/* Strict */}
            <label
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                mode === 'strict'
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="deliveryMode"
                checked={mode === 'strict'}
                onChange={() => handleModeChange('strict')}
                className="mt-1 w-4 h-4 text-red-600 focus:ring-red-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Strict</span>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    Hard Block
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Blocks delivery if balance exceeds the due limit. Only authorized roles (configured below) can override with a mandatory reason. All overrides are audit-logged.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Due Limit */}
        {mode !== 'off' && (
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Due Limit Threshold</h3>
            <p className="text-sm text-gray-500 mb-4">
              Maximum outstanding balance allowed at delivery time. Set to 0 to require full payment.
            </p>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">&#8377;</span>
              <input
                type="number"
                value={dueLimit || ''}
                onChange={(e) => handleLimitChange(e.target.value)}
                min="0"
                step="1"
                className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="0"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {parseFloat(dueLimit) === 0
                ? 'Customer must pay the full amount before delivery.'
                : `Delivery allowed if outstanding balance is within ${getCurrencySymbol()}${parseFloat(dueLimit) || 0}.`}
            </p>
          </div>
        )}

        {/* Override Roles (Strict mode only) */}
        {mode === 'strict' && (
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-purple-600" />
              <h3 className="text-sm font-medium text-gray-900">Override Authorization</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select which roles can override the delivery block with a mandatory reason.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {AVAILABLE_ROLES.map((role) => (
                <label
                  key={role.value}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    overrideRoles.includes(role.value)
                      ? 'border-purple-300 bg-purple-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={overrideRoles.includes(role.value)}
                    onChange={() => handleRoleToggle(role.value)}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{role.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        {mode !== 'off' && (
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">How Delivery Due Control Works:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-blue-700">
                <li>When delivering, the system checks if balance due exceeds the limit</li>
                <li>If within limit: delivery proceeds normally</li>
                {mode === 'warn' && (
                  <>
                    <li>If over limit: warning shows the due amount and limit</li>
                    <li>Employee must acknowledge and provide a reason to proceed</li>
                    <li>All deliveries with outstanding dues are logged for audit</li>
                  </>
                )}
                {mode === 'strict' && (
                  <>
                    <li>If over limit: delivery is blocked for unauthorized roles</li>
                    <li>Authorized roles can override with a mandatory reason</li>
                    <li>All overrides are logged with who, when, amount, and reason</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}

        {mode === 'warn' && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p>
                <strong>Warn mode</strong> allows all employees to deliver with outstanding dues after acknowledgment.
                If you want to restrict who can override, use <strong>Strict mode</strong> instead.
              </p>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={!hasChanges || updateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
